import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../auth/AuthContext';
import {
  Conversation,
  Message,
  createConversation,
  getConversationByUserId,
  getConversationById,
  getMessages,
  sendMessage as sendMessageService,
  getGuestSession,
  setGuestSession,
  clearGuestSession,
  canSendMessage,
  recordMessageSent
} from '../../lib/services/messageService';

// Context interface
interface AskLotusContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  conversation: Conversation | null;
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  guestEmail: string;
  setGuestEmail: (email: string) => void;
  guestName: string;
  setGuestName: (name: string) => void;
  needsEmail: boolean;
  sendMessage: (content: string) => Promise<boolean>;
  startConversation: () => Promise<boolean>;
  startNewConversation: () => void;
  unreadCount: number;
}

// Create context
const AskLotusContext = createContext<AskLotusContextType>({
  isOpen: false,
  setIsOpen: () => {},
  conversation: null,
  messages: [],
  isLoading: false,
  error: null,
  guestEmail: '',
  setGuestEmail: () => {},
  guestName: '',
  setGuestName: () => {},
  needsEmail: false,
  sendMessage: async () => false,
  startConversation: async () => false,
  startNewConversation: () => {},
  unreadCount: 0
});

// Custom hook
export const useAskLotus = () => useContext(AskLotusContext);

// Provider component
export const AskLotusProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  // UI state
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Conversation state
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Guest info
  const [guestEmail, setGuestEmail] = useState('');
  const [guestName, setGuestName] = useState('');

  // Track processed message IDs to prevent duplicates
  const processedMessageIds = useRef<Set<string>>(new Set());

  // Track previous auth state to detect changes
  const prevAuthRef = useRef<{ isAuthenticated: boolean; userId: string | null }>({
    isAuthenticated: false,
    userId: null
  });

  // Determine if we need email (guest without session)
  const needsEmail = !isAuthenticated && !conversation;

  // Reset state when authentication changes (login/logout/switch user)
  useEffect(() => {
    const prevAuth = prevAuthRef.current;
    const currentUserId = user?.id || null;

    // Check if auth state actually changed
    const authChanged = prevAuth.isAuthenticated !== isAuthenticated;
    const userChanged = prevAuth.userId !== currentUserId && (prevAuth.userId !== null || currentUserId !== null);

    if (authChanged || userChanged) {
      // Clear all conversation state
      setConversation(null);
      setMessages([]);
      processedMessageIds.current.clear();
      setUnreadCount(0);
      setError(null);
      setIsOpen(false);

      // If user logged in, clear guest data
      if (isAuthenticated) {
        clearGuestSession();
        setGuestEmail('');
        setGuestName('');
      }

      // Update ref to current state
      prevAuthRef.current = { isAuthenticated, userId: currentUserId };
    }
  }, [isAuthenticated, user?.id]);

  // Load existing conversation for authenticated users
  useEffect(() => {
    const loadConversation = async () => {
      if (isAuthenticated && user?.id) {
        setIsLoading(true);
        const existingConv = await getConversationByUserId(user.id);
        if (existingConv) {
          setConversation(existingConv);
          const msgs = await getMessages(existingConv.id);
          setMessages(msgs);
          msgs.forEach(m => processedMessageIds.current.add(m.id));
        }
        setIsLoading(false);
      }
    };

    loadConversation();
  }, [isAuthenticated, user?.id]);

  // Load guest session on mount
  useEffect(() => {
    if (!isAuthenticated) {
      const session = getGuestSession();
      if (session) {
        setGuestEmail(session.email);
        setGuestName(session.name || '');
        // Load the conversation
        getConversationById(session.conversationId).then(conv => {
          if (conv && conv.status === 'open') {
            setConversation(conv);
            getMessages(conv.id).then(msgs => {
              setMessages(msgs);
              msgs.forEach(m => processedMessageIds.current.add(m.id));
            });
          } else {
            // Conversation closed or not found, clear session
            clearGuestSession();
          }
        });
      }
    }
  }, [isAuthenticated]);

  // Subscribe to realtime messages when conversation exists
  useEffect(() => {
    if (!conversation?.id) return;

    const channelName = `ask-lotus-messages-${conversation.id}-${Date.now()}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversation.id}`
        },
        (payload) => {
          const messageId = payload.new.id;

          // Skip if already processed
          if (processedMessageIds.current.has(messageId)) {
            return;
          }

          processedMessageIds.current.add(messageId);

          const newMessage: Message = {
            id: payload.new.id,
            conversation_id: payload.new.conversation_id,
            sender_type: payload.new.sender_type,
            sender_id: payload.new.sender_id,
            content: payload.new.content,
            created_at: payload.new.created_at
          };

          setMessages(prev => [...prev, newMessage]);

          // Increment unread if widget is closed and message is from staff
          if (!isOpen && payload.new.sender_type === 'staff') {
            setUnreadCount(prev => prev + 1);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversation?.id, isOpen]);

  // Subscribe to conversation status changes (for close/reopen)
  useEffect(() => {
    if (!conversation?.id) return;

    const channelName = `ask-lotus-conversation-status-${conversation.id}-${Date.now()}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversations',
          filter: `id=eq.${conversation.id}`
        },
        (payload) => {
          // Update conversation status in local state
          setConversation(prev => prev ? { ...prev, status: payload.new.status } : null);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversation?.id]);

  // Clear unread count when opening
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  // Start a new conversation
  const startConversation = useCallback(async (): Promise<boolean> => {
    setError(null);

    // Validate email for guests
    if (!isAuthenticated && !guestEmail) {
      setError('Please enter your email address');
      return false;
    }

    if (!isAuthenticated && guestEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) {
      setError('Please enter a valid email address');
      return false;
    }

    setIsLoading(true);

    try {
      const newConv = await createConversation(
        isAuthenticated ? user?.id || null : null,
        !isAuthenticated ? guestEmail : undefined,
        !isAuthenticated ? guestName : undefined
      );

      if (!newConv) {
        setError('Failed to start conversation. Please try again.');
        setIsLoading(false);
        return false;
      }

      setConversation(newConv);
      setMessages([]);

      // Save guest session
      if (!isAuthenticated) {
        setGuestSession({
          conversationId: newConv.id,
          email: guestEmail,
          name: guestName,
          createdAt: new Date().toISOString()
        });
      }

      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('Error starting conversation:', err);
      setError('Failed to start conversation. Please try again.');
      setIsLoading(false);
      return false;
    }
  }, [isAuthenticated, user?.id, guestEmail, guestName]);

  // Send a message
  const sendMessageHandler = useCallback(async (content: string): Promise<boolean> => {
    if (!content.trim()) return false;

    // Rate limiting
    if (!canSendMessage()) {
      setError('Please wait a moment before sending another message');
      return false;
    }

    setError(null);

    // If no conversation exists, start one first
    let currentConversation = conversation;
    if (!currentConversation) {
      const started = await startConversation();
      if (!started) return false;

      // Re-fetch conversation after starting
      if (isAuthenticated && user?.id) {
        currentConversation = await getConversationByUserId(user.id);
      } else {
        const session = getGuestSession();
        if (session) {
          currentConversation = await getConversationById(session.conversationId);
        }
      }

      if (!currentConversation) {
        setError('Failed to create conversation');
        return false;
      }

      setConversation(currentConversation);
    }

    try {
      const newMessage = await sendMessageService(
        currentConversation.id,
        content.trim(),
        'customer',
        isAuthenticated ? user?.id : null
      );

      if (!newMessage) {
        setError('Failed to send message. Please try again.');
        return false;
      }

      recordMessageSent();

      // Add to local state immediately (realtime will handle duplicates)
      if (!processedMessageIds.current.has(newMessage.id)) {
        processedMessageIds.current.add(newMessage.id);
        setMessages(prev => [...prev, newMessage]);
      }

      return true;
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
      return false;
    }
  }, [conversation, startConversation, isAuthenticated, user?.id]);

  // Start a new conversation (used when previous one was closed)
  const startNewConversation = useCallback(() => {
    // Clear current conversation state
    setConversation(null);
    setMessages([]);
    processedMessageIds.current.clear();
    setError(null);

    // Clear guest session if not authenticated
    if (!isAuthenticated) {
      clearGuestSession();
      // Keep email/name for convenience so they don't have to re-enter
    }
  }, [isAuthenticated]);

  return (
    <AskLotusContext.Provider
      value={{
        isOpen,
        setIsOpen,
        conversation,
        messages,
        isLoading,
        error,
        guestEmail,
        setGuestEmail,
        guestName,
        setGuestName,
        needsEmail,
        sendMessage: sendMessageHandler,
        startConversation,
        startNewConversation,
        unreadCount
      }}
    >
      {children}
    </AskLotusContext.Provider>
  );
};

export default AskLotusContext;
